// Must be first: installs the global `Buffer` polyfill that the Solana payment
// stack (@reown/appkit-adapter-solana, @solana/web3.js, @solana/spl-token) needs.
import "./polyfills.ts";

import React from "react";
import { installGlobalErrorReporting } from "./lib/errorReporting.ts";
import { installDomMutationGuard } from "./lib/domMutationGuard.ts";
import ReactDOM from "react-dom/client";
import "@radix-ui/themes/styles.css";
import "./index.css";

/* Theme CSS - Organized by module */
// Base theme
import "./theme-variables.css";
import "./theme-animations.css";
import "./theme-buttons.css"; // Professional button design system
// Headings
// (theme-headings-base.css was deleted: all 143 of its selectors — and all 19 inside its
//  media blocks — were also defined in theme-components-navigation.css below, which loads
//  later and therefore won every one of them. It was 914 lines of dead CSS carrying 87
//  phantom !important declarations.)
import "./theme-headings-extended.css";
// Component styles (organized by function)
import "./theme-components-navigation.css";
import "./theme-components-app-shell.css";
import "./theme-game-shell.css";
import "./theme-content-sections.css";
import "./theme-content-panel.css";
import "./theme-utilities.css";
// Page-specific styles
import "./theme-home.css";
import "./theme-home-battle-demo.css";
import "./theme-home-bands.css";
import "./theme-home-hero.css";
import "./theme-home-type.css";
import "./theme-my-pets.css";
import "./theme-pet-profile.css";
import "./theme-pet-detail-page.css";
import "./theme-adopt-page.css";
import "./theme-dialog-overrides.css";
import "./theme-trainer-profile.css";
import "./theme-login.css";
import "./theme-payment-network.css";
import "./theme-i18n.css";
// Must stay LAST: caps expensive backdrop blurs on phone viewports by
// overriding the rules above. See theme-mobile-performance.css.
import "./theme-mobile-performance.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Theme } from "@radix-ui/themes";
import App from "./App.tsx";
import { AccountProvider } from "./auth/AccountProvider.tsx";
import { ToastProvider } from "./components/ToastContainer.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { I18nProvider } from "./i18n/I18nProvider.tsx";

const queryClient = new QueryClient();

// Forward every uncaught error / unhandled promise rejection to the backend → Telegram.
installGlobalErrorReporting();
// Before the first render: page translation must not be able to crash a React commit.
installDomMutationGuard();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {/* Outermost, so every screen (the error page included) renders in the player's language.
        A locale change re-renders in place: it never remounts the game below it. */}
    <I18nProvider>
    <ErrorBoundary>
      <Theme
        appearance="light"
        accentColor="grass"
        grayColor="slate"
        className="suikamon-root"
      >
        {/* Web2 (Suikamon 20): no wallet provider is mounted — sign-in is Supabase Auth only
            (AccountProvider). The wagmi / Reown / dApp-kit configs (wagmi.ts, dapp-kit.ts) and
            everything that needs them are PARKED, not deleted; nothing in the running game may
            call a wallet hook, or it throws for want of its provider. */}
        <QueryClientProvider client={queryClient}>
          <AccountProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </AccountProvider>
        </QueryClientProvider>
      </Theme>
    </ErrorBoundary>
    </I18nProvider>
  </React.StrictMode>,
);
